import { edkt, checkConsentStatus, waitForConsent } from '@airgrid/edgekit';
import { logAnalytics } from './analytics';
import { registerBidResponseEventCallback } from './analytics/bidEventStream';
import {
  generateLoadId,
  debug,
  getFeaturesAndAudiences,
  logError,
  hasCmpBeenShownAndConsentRejected,
} from './utils';

declare const VERSION: string;
declare const EDKT_VERSION: string;

const AIRGRID_VENDOR_ID = 782;
const vendorIds = [AIRGRID_VENDOR_ID];
const edktInitializor = (<any>window).edktInitializor || {};
const apiKey = edktInitializor.apiKey || '';
const accountId = edktInitializor.accountId || '';
const publisherId = edktInitializor.publisherId || '';
const omitGdprConsent = edktInitializor.disableGdpr || false;

const loadId = generateLoadId();
edktInitializor.loadId = loadId;

const run = async (): Promise<void> => {
  let hasConsent = false;

  edktInitializor.debug = {
    omitGdprConsent,
    hasConsent,
    VERSION,
    EDKT_VERSION,
  };

  if (omitGdprConsent) {
    hasConsent = true;
  } else {
    let consentStatus;
    try {
      consentStatus = await checkConsentStatus(vendorIds);
    } catch (err) {
      throw new Error(err.message);
    }
    if (!hasConsent && hasCmpBeenShownAndConsentRejected(consentStatus)) {
      await logAnalytics(
        consentStatus.hasConsent,
        edkt.getMatchedAudiences(),
        false
      );

      debug('No consent granted', { ...edktInitializor.debug });

      return;
    }
  }

  hasConsent = hasConsent ? true : await waitForConsent(vendorIds);

  if (!hasConsent) {
    await logAnalytics(hasConsent, edkt.getMatchedAudiences(), false);

    debug('Waited for consent, not granted', { ...edktInitializor.debug });
    return;
  }

  const {
    featuresResponse,
    audienceDefinitions,
  } = await getFeaturesAndAudiences(apiKey, accountId, publisherId, loadId);
  const features = featuresResponse?.features || undefined;
  const metadata = featuresResponse?.metadata || undefined;

  await edkt.run({
    pageFeatures: features,
    pageMetadata: metadata,
    audienceDefinitions,
    vendorIds,
    omitGdprConsent,
  });

  await Promise.all([
    registerBidResponseEventCallback(apiKey, loadId),
    logAnalytics(
      hasConsent,
      edkt.getMatchedAudiences(),
      featuresResponse?.featuresAvailable
    ),
  ]);
};

(async (): Promise<void> => {
  try {
    await run();
  } catch (err) {
    await logError(loadId, apiKey, err);
  }
})();
